import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { BRANCH_LABEL } from "../../components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const inp = "w-full bg-muted/60 border h-9 px-2 text-sm";
const BLOOD = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
const BR = ["DEL", "MUM", "*"];
const FIELDS = ["name", "official_email", "phone", "designation", "department", "branch", "reporting_manager", "joined_on", "dob",
  "emergency_name", "emergency_relation", "emergency_phone", "address", "blood_group", "pan", "aadhaar", "bank_name", "bank_account", "ifsc"];

const F = ({ k, label, f, set, type = "text", ph, opts }) => (
  <label className="text-xs"><span className="ax-label">{label}</span>
    {opts ? <select data-testid={`emp-${k}`} className={inp} value={f[k] || ""} onChange={(e) => set(k, e.target.value)}>
      <option value="">—</option>{opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
      : <input data-testid={`emp-${k}`} type={type} placeholder={ph} className={inp} value={f[k] || ""} onChange={(e) => set(k, e.target.value)} />}
  </label>
);

const Section = ({ title, children }) => (
  <div className="border-t pt-3"><div className="ax-label mb-2 text-primary">{title}</div><div className="grid sm:grid-cols-3 gap-3">{children}</div></div>
);

const AccessBox = ({ a, setA, roles, hasLogin }) => (
  <div className="border-t pt-3" data-testid="emp-access-section">
    <label className="flex items-center gap-2 text-sm mb-2"><input type="checkbox" data-testid="emp-access-toggle" checked={a.on} onChange={(e) => setA({ ...a, on: e.target.checked })} />
      {hasLogin ? "Change app access" : "Give app login (they sign in with Google using the official email, or set a password via 'Forgot password')"}</label>
    {a.on && <div className="grid sm:grid-cols-3 gap-3">
      <label className="text-xs"><span className="ax-label">Role</span>
        <select data-testid="emp-access-role" className={inp} value={a.role_key || ""} onChange={(e) => setA({ ...a, role_key: e.target.value })}>
          <option value="">Select</option>{roles.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}</select></label>
      <label className="text-xs"><span className="ax-label">Name in sales/order sheets</span><input data-testid="emp-access-staff" className={inp} placeholder="e.g. Akshita" value={a.staff_name || ""} onChange={(e) => setA({ ...a, staff_name: e.target.value })} /></label>
      <div className="text-xs"><span className="ax-label">Branches</span><div className="flex gap-3 mt-2">{BR.map((b) => (
        <label key={b} className="flex items-center gap-1"><input type="checkbox" data-testid={`emp-access-branch-${b}`} checked={a.branches.includes(b)}
          onChange={() => setA({ ...a, branches: a.branches.includes(b) ? a.branches.filter((x) => x !== b) : [...a.branches, b] })} />{BRANCH_LABEL[b]}</label>))}</div></div>
    </div>}
  </div>
);

export const EmployeeDialog = ({ code, onClose, onSaved }) => {
  const { can } = useAuth();
  const isNew = !code;
  const [f, setF] = useState({});
  const [orig, setOrig] = useState({});
  const [login, setLogin] = useState(null);
  const [roles, setRoles] = useState([]);
  const [next, setNext] = useState("");
  const [a, setA] = useState({ on: false, branches: ["MUM"] });
  const [depts, setDepts] = useState([]);
  useEffect(() => {
    api.get("/departments").then((r) => setDepts(r.data.items.map((d) => d.name))).catch(() => {});
    api.get("/hr/roles").then((r) => setRoles(r.data)).catch((e) => toast.error(`Couldn't load roles: ${errMsg(e)}`));
    if (isNew) { api.get("/hr/next-id").then((r) => setNext(r.data.next)).catch(() => {}); return; }
    api.get(`/hr/employees/${encodeURIComponent(code)}`).then((r) => {
      setF(r.data); setOrig(r.data); setLogin(r.data.login);
      if (r.data.login) setA({ on: false, role_key: r.data.login.role_key, branches: r.data.login.branches || ["MUM"], staff_name: r.data.login.staff_name || "" });
    }).catch((e) => toast.error(errMsg(e)));
  }, [code, isNew]);
  const set = (k, v) => setF({ ...f, [k]: v });
  const readOnly = !isNew && !can("hr", "update");
  const access = a.on ? { role_key: a.role_key, branches: a.branches, staff_name: a.staff_name } : null;
  const save = async () => {
    if (a.on && !a.role_key) { toast.error("Pick a role for app access"); return; }
    try {
      if (isNew) await api.post("/hr/employees", { ...Object.fromEntries(FIELDS.map((k) => [k, f[k] || null])), emp_code: f.emp_code || null, access });
      else {
        const changed = Object.fromEntries(FIELDS.filter((k) => (f[k] || "") !== (orig[k] || "")).map((k) => [k, f[k] || ""]));
        if (access) await api.post(`/hr/employees/${encodeURIComponent(code)}/access`, access);
        if (Object.keys(changed).length) await api.patch(`/hr/employees/${encodeURIComponent(code)}`, changed);
      }
      toast.success("Employee saved");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const p = { f, set };
  const deptOpts = [...depts, ...(f.department && !depts.some((d) => d.toLowerCase() === f.department.trim().toLowerCase()) ? [f.department] : [])].map((d) => [d, d]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-card" data-testid="employee-dialog">
        <DialogHeader><DialogTitle className="font-display">{isNew ? "Add employee" : `${orig.name || ""} · ID ${code}`}</DialogTitle></DialogHeader>
        <fieldset disabled={readOnly} className="space-y-4">
          <Section title="Basic details">
            {isNew ? <F k="emp_code" label="Employee ID" ph={next ? `Leave blank for next: ${next}` : "Leave blank for next number"} {...p} />
              : <div className="text-xs"><span className="ax-label">Employee ID</span><div className="h-9 flex items-center font-mono" data-testid="emp-code-fixed">{code}</div></div>}
            <F k="name" label="Full name *" {...p} /><F k="official_email" label="Official email" ph="name@axiomviscom.in" {...p} />
            <F k="phone" label="Phone" {...p} /><F k="designation" label="Designation" {...p} /><F k="department" label="Department" opts={deptOpts} {...p} />
            <F k="branch" label="Branch" opts={[["DEL", "Delhi (FBD)"], ["MUM", "Mumbai"]]} {...p} /><F k="reporting_manager" label="Reporting manager" {...p} />
            <F k="joined_on" label="Joining date" type="date" {...p} />
          </Section>
          <Section title="Emergency contact"><F k="emergency_name" label="Name" {...p} /><F k="emergency_relation" label="Relation" {...p} /><F k="emergency_phone" label="Phone" {...p} /></Section>
          <Section title="Personal details">
            <F k="dob" label="Date of birth (shows birthday on Home)" type="date" {...p} /><F k="blood_group" label="Blood group" opts={BLOOD.map((b) => [b, b])} {...p} />
            <F k="address" label="Home address" {...p} />
          </Section>
          <Section title="ID and bank details (HR and founders only)">
            <F k="pan" label="PAN" ph="ABCDE1234F" {...p} /><F k="aadhaar" label="Aadhaar" ph="12 digits" {...p} /><F k="bank_name" label="Bank name" {...p} />
            <F k="bank_account" label="Account number" {...p} /><F k="ifsc" label="IFSC" ph="HDFC0001234" {...p} />
          </Section>
          {login && <div className="text-xs text-muted-foreground" data-testid="emp-login-info">App login: {roles.find((r) => r.key === login.role_key)?.name || login.role_key} · {login.status}</div>}
          <AccessBox a={a} setA={setA} roles={roles} hasLogin={!!login} />
        </fieldset>
        <div className="flex justify-end gap-2"><button onClick={onClose} className="border h-9 px-4 text-sm">Close</button>
          {!readOnly && <button data-testid="employee-save" onClick={save} className="bg-primary text-primary-foreground h-9 px-4 text-sm">Save</button>}</div>
      </DialogContent>
    </Dialog>
  );
};
